





























































































































































































import { DataProxy } from 'apollo-cache';
import gql from 'graphql-tag';
import * as R from 'ramda';
import Vue from 'vue';

import { Category } from '@/core/entity';

const query = gql`
  query {
    categories: listCategory {
      id
      name
      categories {
        id
        name
        categories {
          id
          name
          categories {
            id
            name
            categories {
              id
              name
              categories {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

interface NodeData {
  key: string;
  title: string;
  children: NodeData[];
  scopedSlots: { [key: string]: string };
  selectable: boolean;
  path: string;
  isLeaf: boolean;
}

export default Vue.extend({
  apollo: {
    categories: {
      query,
      fetchPolicy: 'cache-first',
    },
  },

  data() {
    return {
      categories: [] as Category[],
      category: '',
      loading: false,
      selected: null as string | null,
    };
  },

  computed: {
    treeData(): NodeData[] {
      const mapIndexed = R.addIndex<Category, NodeData>(R.map);
      const toNodeData = (parentPath: string | null) => {
        return (c: Category, idx: number): NodeData => {
          const path = parentPath ? `${parentPath}.${idx}` : `${idx}`;
          const children = mapIndexed(
            toNodeData(path),
            c.categories,
          ) as NodeData[];
          return {
            key: c.id,
            title: c.name,
            children,
            scopedSlots: { title: 'custom' },
            selectable: false,
            path,
            isLeaf: children.length === 0,
          };
        };
      };

      return mapIndexed(toNodeData(null), this.categories) as NodeData[];
    },
  },

  methods: {
    getCategoryFromPath(idxs: number[], list: Category[]) {
      return R.reduce(
        (acc: Category | null, el) => {
          if (acc === null) {
            acc = list[el];
          } else {
            acc = acc.categories[el];
          }

          return acc;
        },
        null,
        idxs,
      );
    },

    handleAdd(id: string, path: string) {
      // path contains indexes into categories array
      // e.g. 0.0.0 = this.categories[0].categories[0].categories[0]
      const idxs = R.pipe(
        R.split('.'),
        R.map((s) => parseInt(s, 10)),
      )(path);

      // when adding a category, we add it as part of its children categories
      const self = this.getCategoryFromPath(idxs, this.categories);
      const tempId = `new.${Date.now()}`;
      self!.categories = R.append(
        { id: tempId, name: '', categories: [] },
        R.reject((r) => r.id.startsWith('new.'), self!.categories),
      );

      this.selected = tempId;
    },

    handleEdit(id: string, title: string) {
      this.selected = id;
      this.category = title;
    },

    handleRemove(id: string, path: string) {
      // path contains indexes into categories array
      // e.g. 0.0.0 = this.categories[0].categories[0].categories[0]
      const idxs = R.pipe(
        R.split('.'),
        R.map((s) => parseInt(s, 10)),
      )(path);
      const categoryId = this.selected!;

      const parent = this.getCategoryFromPath(
        R.take(idxs.length - 1, idxs),
        this.categories,
      );

      if (parent === null) {
        // cannot delete root category
        this.$message.error('Cannot remove category \'Intrack\'.');
        return;
      }

      if (categoryId.startsWith('new.')) {
        // immediately remove if it is not a saved category
        parent!.categories = R.reject(
          R.propEq('id', categoryId),
          parent!.categories,
        );
        return;
      }

      if (parent!.categories[idxs[idxs.length - 1]].categories.length !== 0) {
        // do not allow deleting of categories with child categories
        this.$message.error(
          'Cannot remove a category that contains other child categories',
        );
        return;
      }

      // remove category
      this.$coreApp.category.remove(
        categoryId,
        this.presenter('Category removed.', 'Unable to remove category:'),
        this.removedUpdater(idxs),
      );

      this.selected = null;
    },

    handleSave(path: string) {
      // path contains indexes into categories array
      // e.g. 0.0.0 = this.categories[0].categories[0].categories[0]
      const idxs = R.pipe(
        R.split('.'),
        R.map((s) => parseInt(s, 10)),
      )(path);

      const name = this.category.trim();
      if (name.length === 0) {
        this.$message.error(`Category name cannot be empty.`);
        return;
      }

      const categoryId = this.selected!;

      const parent = this.getCategoryFromPath(
        R.take(idxs.length - 1, idxs),
        this.categories,
      );

      if (!categoryId.startsWith('new.')) {
        // edit category
        this.$coreApp.category.update(
          categoryId,
          {
            name,
            parentId: parent!.id,
          },
          this.presenter('Category updated.', 'Unable to update category:'),
          this.updatedUpdater(idxs),
        );
      } else {
        // create category
        this.$coreApp.category.create(
          {
            name,
            parentId: parent!.id,
          },
          this.presenter('Category created.', 'Unable to create category:'),
          this.createdUpdater(idxs),
        );
      }
    },

    createdUpdater(idxs: number[]) {
      return (store: DataProxy, res: { data: { newCategory: Category } }) => {
        const data: { categories: Category[] } | null = store.readQuery({
          query,
        });

        // when creating a category, we need to change the category with the id
        // __new__ in parent's children categories to the created id
        const parent = this.getCategoryFromPath(
          R.take(idxs.length - 1, idxs),
          data!.categories,
        );
        parent!.categories = R.update(
          R.last(idxs)!,
          {
            ...res.data.newCategory,
            categories: [],
          },
          parent!.categories,
        );

        store.writeQuery({ query, data });
      };
    },

    removedUpdater(idxs: number[]) {
      return (
        store: DataProxy,
        res: { data: { removeCategory: Category } },
      ) => {
        const data: { categories: Category[] } | null = store.readQuery({
          query,
        });

        // when removing a category, we remove it from parent's children categories
        const parent = this.getCategoryFromPath(
          R.take(idxs.length - 1, idxs),
          data!.categories,
        );
        parent!.categories = R.reject(
          R.propEq('id', res.data.removeCategory.id),
          parent!.categories,
        );

        store.writeQuery({ query, data });
      };
    },

    updatedUpdater(idxs: number[]) {
      return (
        store: DataProxy,
        res: { data: { updateCategory: Category } },
      ) => {
        const data: { categories: Category[] } | null = store.readQuery({
          query,
        });

        const parent = this.getCategoryFromPath(
          R.take(idxs.length - 1, idxs),
          data!.categories,
        );
        const previous = parent!.categories[R.last(idxs)!];
        parent!.categories = R.update(
          R.last(idxs)!,
          {
            ...previous,
            ...res.data.updateCategory,
          },
          parent!.categories,
        );

        store.writeQuery({ query, data });
      };
    },

    presenter(success: string, failure: string) {
      return (err: Error | null, result: Category | null, loading: boolean) => {
        this.loading = loading;
        if (err) {
          this.$message.error(`${failure}: ${err.message}.`);
        } else if (result !== null) {
          this.$message.success(success);
          this.category = '';
          this.selected = null;
        }
      };
    },
  },
});
